import { Box, LinearProgress, Link, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useLink } from '~/hooks/link';

/** Health percentage at which all devices are online (displays green) */
const HEALTH_EXCELLENT_THRESHOLD = 100;
/** Health percentage threshold for warning state (displays yellow when >= this value) */
const HEALTH_WARNING_THRESHOLD = 70;

export interface DeviceGroupHealth {
  id: number;
  name: string;
  totalDevices: number;
  onlineCount: number;
}

export interface HealthByGroupChartProps {
  groups: DeviceGroupHealth[];
  maxGroups?: number;
}

export const HealthByGroupChart = ({ groups, maxGroups = 12 }: HealthByGroupChartProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const link = useLink();

  const sortedGroups = groups
    .filter((g) => g.totalDevices > 0)
    .sort((a, b) => {
      // Sort by health percentage (worst first)
      const aPercent = a.onlineCount / a.totalDevices;
      const bPercent = b.onlineCount / b.totalDevices;
      if (aPercent !== bPercent) return aPercent - bPercent;
      // Tie-breaker: more total devices first
      return b.totalDevices - a.totalDevices;
    })
    .slice(0, maxGroups);

  const getHealthColor = (percentage: number): string => {
    if (percentage === HEALTH_EXCELLENT_THRESHOLD) return theme.palette.success.main;
    if (percentage >= HEALTH_WARNING_THRESHOLD) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="h2" variant="h5">
          {t('pages.dashboard.health_by_group.title')}
        </Typography>
        <Link component={RouterLink} to={link('/device-groups')} variant="body1">
          {t('pages.dashboard.view_all')}
        </Link>
      </Box>
      {sortedGroups.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t('pages.dashboard.health_by_group.no_groups')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {sortedGroups.map((group) => {
            const percentage =
              group.totalDevices > 0
                ? Math.round((group.onlineCount / group.totalDevices) * 100)
                : 0;

            return (
              <Box
                key={group.id}
                component={RouterLink}
                to={link(`/devices?groups=[${group.id}]`)}
                sx={{
                  display: 'block',
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { backgroundColor: theme.palette.grey[50] },
                  p: 1,
                  mx: -1,
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '60%',
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {group.onlineCount}/{group.totalDevices} ({percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  // The text above already gives the percentage, and this bar has no name.
                  aria-hidden
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor:
                      percentage === 0 ? theme.palette.error.main : theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getHealthColor(percentage),
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};
